import React, { useMemo, forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
//#endregion
//#region types
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { FileConstants, FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { ConnectInvoiceFormContainerProps } from "models/propTypes/timeReportingProjectPropTypes";
import cssExports from "theme/styles/main.module.scss";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { FileViewerContainer } from "components/common";
import ConnectInvoiceForm from "./connectInvoiceForm";
import { Portal } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { useInvoiceService, useTimeReportingProjectService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const ConnectInvoiceFormContainer: ForwardRefRenderFunction<FormHandles, ConnectInvoiceFormContainerProps> = (
  props,
  ref
) => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const invoiceService = useInvoiceService();

  const [file, setFile] = React.useState<ImageMeta>();
  const [leftTo, setLeftTo] = React.useState<number>(0);

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (formRef?.current ? formRef.current : ({} as FormHandles)));

  const possibleInvoiceStates = [
    WintSuperkollAPIModel.InvoiceStatus.Unpaid,
    WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent,
    WintSuperkollAPIModel.InvoiceStatus.OverdueReminderSent,
    WintSuperkollAPIModel.InvoiceStatus.Paid,
    WintSuperkollAPIModel.InvoiceStatus.Collection,
  ];

  const initialFilter: WintSuperkollAPIModel.InvoiceFilter = {
    statuses: possibleInvoiceStates,
    page: 0,
    numPerPage: 20,
    orderByDescending: true,
    thenByDescending: true,
    isAutoPaginating: true,
  };

  const validationSchema = useMemo(() => {
    const scheme = {};
    const memberGroups = UtilsHelper.groupBy(
      props.memberCategories,
      FormFieldConstants.TimeReportingProject.ConnectInvoiceMemberCategory.MemberId
    );
    memberGroups.forEach((m) => {
      (scheme as any)[`${m.key}`] = Yup.array()
        .test(
          "validateLeftToInvoice",
          LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.DividedMoreThanInvoiceAmount
          ),
          () => {
            return leftTo >= 0;
          }
        )
        .of(
          Yup.object<TimeReportingProjectFormTypes.ConnectInvoiceCategory>({
            categoryId: Yup.number(),
            categoryName: Yup.string(),
            amount: Yup.number()
              .required(TranslationKeys.Validations.Required)
              .moreThan(0, TranslationKeys.Validations.Required),
          })
        );
    });

    formRef.current?.validate && formRef.current.validate();
    return Yup.object<TimeReportingProjectFormTypes.ConnectInvoice>({
      ...scheme,
      selectedItems: Yup.array()
        .required(TranslationKeys.Validations.Required)
        .of(
          Yup.object<SelectionItem>({
            id: Yup.number(),
            value: Yup.string(),
          })
        ),
    });
  }, [leftTo]);

  const handleFormSubmit = async (values: TimeReportingProjectFormTypes.ConnectInvoice) => {
    const personCategoryTimes = [...(props.invoiceDocumentation?.personCategoryTimes ?? [])];

    const connectInvoicePersonCategories: ApiModel.ConnectInvoicePersonCategoryTimes[] = [];

    personCategoryTimes.forEach((pct) => {
      const memberCategories: TimeReportingProjectFormTypes.ConnectInvoiceCategory[] = (values as any)[
        `${pct.personId}`
      ];
      const category = memberCategories.find((mc) => mc.categoryId === pct.categoryId);
      connectInvoicePersonCategories.push({ ...pct, amount: category?.amount ?? 0 });
    });

    const selectedInvoiceId = Number(values.selectedItems && values.selectedItems[0].id);
    const documentation: ApiModel.ConnectInvoiceDocumentation = {
      timeReportingProjectId: props.projectId,
      invoiceId: selectedInvoiceId,
      fromDate: props.invoiceDocumentation?.fromDate ?? new Date(),
      toDate: props.invoiceDocumentation?.toDate ?? new Date(),
      personCategoryTimes: connectInvoicePersonCategories,
      ignoreAllInvoiceItemsConnected: true,
    };

    await timeReportingProjectService.connectInvoice(props.projectId ?? "", documentation);

    props.onConnectSubmitSuccess(selectedInvoiceId);
  };

  React.useEffect(() => {
    getInitialResultList();
  }, []);

  const getInitialResultList = async () => {
    formRef.current?.setValues && formRef.current.setValues({ loading: true, selectedItems: [] });
    setFile(undefined);

    const values: TimeReportingProjectFormTypes.ConnectInvoice =
      formRef.current?.getValues && formRef.current?.getValues();

    const updatedValues = { ...values, loading: false, selectedItems: [], invoiceAmount: 0, currency: undefined };
    const suggestions = await getInvoiceItems(initialFilter, "", true);
    const all = await getInvoiceItems(initialFilter, "");

    updatedValues.invoiceListSuggestions = suggestions;
    updatedValues.invoiceList = all;

    formRef.current?.setValues && formRef.current.setValues(updatedValues);
  };

  const onInvoiceSelect = (item: SelectionItem) => {
    const invoice = item.data as WintSuperkollAPIModel.InvoiceListItem;
    const amount = invoice.totalAmount;
    const values = formRef.current?.getValues && formRef.current.getValues();
    const updatedValues = { ...values } as TimeReportingProjectFormTypes.ConnectInvoice;

    formRef.current?.setValues &&
      formRef.current?.setValues({
        ...updatedValues,
        selectedItems: [item],
        invoiceAmount: amount,
        currency: invoice.currency,
      });
    invoiceService.getInvoicePDFPreview(Number(item.id.toString())).then((res) => {
      const data = res.data as unknown as string;
      setFile({ data: data, contentType: "html", id: "invoiceHtml", numberOfPages: 1 });
    });
  };

  const getInvoiceItems = async (
    filter: WintSuperkollAPIModel.InvoiceFilter,
    searchString: string,
    suggestions?: boolean
  ): Promise<SelectionItem[]> => {
    if (!UtilsHelper.isEmpty(searchString) && Number(searchString) === undefined) {
      return [];
    }

    const updatedFilter = { ...filter, serialNumberSearchText: searchString };
    if (suggestions) {
      updatedFilter.customerId = props.project?.customerId;
    }

    const invoices = (await invoiceService.getByFilter(updatedFilter).then((res) => res.data.items)) ?? [];

    return invoices.map(
      (invoiceInfo) =>
        ({
          id: invoiceInfo.id,
          value: invoiceInfo.serialNumber?.toString(),
          data: invoiceInfo,
          icon: IconDefinitionConstants.WPaperPlane,
        } as SelectionItem)
    );
  };

  const getResultList = (filter: WintSuperkollAPIModel.InvoiceFilter, str: string, suggestions?: boolean) => {
    formRef.current?.setValues && formRef.current.setValues({ loading: true, selectedItems: [] });
    getInvoiceItems(filter, str, suggestions).then((result) => {
      const values: TimeReportingProjectFormTypes.ConnectInvoice =
        formRef.current?.getValues && formRef.current?.getValues();

      const updatedValues = { ...values, loading: false, selectedItems: [], invoiceAmount: 0, currency: undefined };
      if (suggestions) {
        updatedValues.invoiceListSuggestions = result;
      } else {
        updatedValues.invoiceList = result;
      }
      formRef.current?.setValues && formRef.current.setValues(updatedValues);
    });
  };

  const getResulListOnSearch = (str: string, suggestions?: boolean) => {
    setFile(undefined);
    getResultList(initialFilter, str, suggestions);
  };

  const getResultListDebounced = useDebouncedCallback(getResulListOnSearch, 700);

  const onSuggestionsSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value;
    getResultListDebounced(str, true);
  };

  const onAllSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value;
    getResultListDebounced(str);
  };

  const getInitialValues = () => {
    const vals: TimeReportingProjectFormTypes.ConnectInvoice = {};

    const memberGroups = UtilsHelper.groupBy(
      props.memberCategories,
      FormFieldConstants.TimeReportingProject.ConnectInvoiceMemberCategory.MemberId
    );

    memberGroups.forEach((m) => {
      (vals as any)[`${m.key}`] = m.value.map(
        (item) =>
          ({
            categoryId: item.categoryId,
            categoryName: item.categoryName,
            amount: 0,
          } as TimeReportingProjectFormTypes.ConnectInvoiceCategory)
      );
    });

    return vals;
  };

  const onLeftToInvoiceChange = (amount: number) => {
    setLeftTo(amount);
  };

  const previewElement = document.getElementById(FileConstants.DialogFilePreviewPortalId);
  return (
    <>
      {file && (
        <Portal container={previewElement}>
          <FileViewerContainer meta={[file]} className={cssExports.uploaderPreviewContainer} />
        </Portal>
      )}
      <ConnectInvoiceForm
        {...props}
        ref={formRef}
        defaultValues={getInitialValues()}
        validationSchema={validationSchema}
        handleSubmit={handleFormSubmit}
        onFormValidationChange={props.onFormValidationChange}
        onInvoiceSelect={onInvoiceSelect}
        onSuggestionsSearchTextChange={onSuggestionsSearchTextChange}
        onAllSearchTextChange={onAllSearchTextChange}
        getInvoiceItems={getInvoiceItems}
        defaultFilter={initialFilter}
        onLeftToInvoiceChange={onLeftToInvoiceChange}
      />
    </>
  );
};

export default forwardRef(ConnectInvoiceFormContainer);
